import { Button, Modal } from "@mantine/core";
import type React from "react";
import { useState } from "react";

export interface PortalTab {
  key: string;
  label: string;
}

interface PortalLayoutProps {
  tabs: PortalTab[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  onBackToHome: () => void;
  onLogout?: () => void;
  error?: string | null;
  onDismissError?: () => void;
  contentClassName?: string;
  children: React.ReactNode;
}

const PortalLayout: React.FC<PortalLayoutProps> = ({
  tabs,
  activeTab,
  onTabChange,
  onBackToHome,
  onLogout,
  error,
  onDismissError,
  contentClassName,
  children,
}) => {
  const [logoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);

  return (
    <div className="font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif] bg-background min-h-screen flex flex-col text-foreground">
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 border-b border-destructive/25 flex items-center">
          <span className="flex-1">{error}</span>
          {onDismissError && (
            <button
              onClick={onDismissError}
              className="bg-transparent border-none text-destructive ml-4 cursor-pointer text-xl hover:opacity-70"
              aria-label="Dismiss error"
            >
              {"\u00D7"}
            </button>
          )}
        </div>
      )}

      <header className="bg-card shadow-sm border-b border-border sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto flex justify-between items-center py-2.5 px-3 sm:px-5">
          <img
            src="/images/logo.png"
            alt="Pheefee Logo"
            className="w-10 h-10 object-contain rounded-lg cursor-pointer"
            onClick={onBackToHome}
          />
          {onLogout && (
            <Button
              onClick={() => setLogoutModalOpen(true)}
              color="red"
              variant="outline"
              radius="md"
            >
              Logout
            </Button>
          )}
        </div>

        <nav className="max-w-[1200px] mx-auto flex px-3 sm:px-5 gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                className={`px-5 py-3 bg-transparent border-none cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 rounded-t-md mr-1 ${
                  isActive
                    ? "text-primary border-b-primary font-semibold bg-primary/10"
                    : "text-muted-foreground border-b-transparent hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main
        className={
          contentClassName ??
          "flex-1 max-w-[1200px] mx-auto w-full py-4 sm:py-[30px] px-3 sm:px-5 bg-background"
        }
      >
        {children}
      </main>

      {onLogout && (
        <Modal
          opened={logoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          title="Confirm Logout"
          centered
          size="sm"
        >
          <p className="text-foreground mb-6">Are you sure you want to logout?</p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setLogoutModalOpen(false)} radius="md">
              Cancel
            </Button>
            <Button color="red" onClick={onLogout} radius="md">
              Logout
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PortalLayout;
